import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { NavBar } from "@/components/NavBar";
import { SEO } from "@/components/SEO";
import { ArrowLeft, CheckCircle, Camera, Building2, Upload, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL || "";

const FORM_KINDS = {
  "second-shooter": {
    label: "I'm a second shooter",
    seoName: "Second Shooters",
    blurb: "Second photographers and videographers, assistants, and drone operators.",
    icon: Camera,
  },
  studio: {
    label: "I'm a studio",
    seoName: "Studios",
    blurb: "Studios and lead photographers or videographers who build a team for the day.",
    icon: Building2,
  },
};

/** Landing page: choose which form to fill in. */
function ChooseForm() {
  return (
    <div className="max-w-2xl mx-auto px-6 py-24">
      <Link
        to="/"
        className="inline-flex items-center gap-2 text-white/40 hover:text-white transition-colors mb-8 text-sm"
      >
        <ArrowLeft className="w-4 h-4" /> Back to Home
      </Link>

      <p className="text-[11px] uppercase tracking-[3px] text-[#e8594a] font-mono mb-4">
        Social series
      </p>
      <h1 className="text-4xl font-bold mb-4 tracking-tight">Photographer of the Week</h1>
      <p className="text-white/50 mb-10 leading-relaxed">
        Every week we feature someone from the GottaSecond community — the second shooters and
        assistants who make the day work, and the studios who build the teams. Tell us about
        yourself and your work, and we may feature you.
      </p>

      <div className="grid sm:grid-cols-2 gap-4">
        {Object.entries(FORM_KINDS).map(function ([kind, meta]) {
          const Icon = meta.icon;
          return (
            <Link
              key={kind}
              to={"/photographer-of-the-week/" + kind}
              className="block bg-white/[0.03] border border-white/10 rounded-lg p-6 hover:bg-white/[0.06] hover:border-white/20 transition-colors"
              data-testid={"potw-choose-" + kind}
            >
              <Icon className="w-6 h-6 text-[#e8594a] mb-4" />
              <h2 className="text-lg font-semibold mb-1">{meta.label}</h2>
              <p className="text-sm text-white/45">{meta.blurb}</p>
            </Link>
          );
        })}
      </div>
    </div>
  );
}

/** Conditional fields appear only when the answer they depend on calls for them. */
function isVisible(field, answers) {
  const rule = field.show_if;
  if (!rule) return true;
  const value = answers[rule.field];
  if (rule.in) return rule.in.indexOf(value) !== -1;
  return value === rule.equals;
}

function isRequired(field, answers) {
  if (field.required) return true;
  const rule = field.required_if;
  if (!rule) return false;
  return answers[rule.field] === rule.equals;
}

/** Uploader for the JPEG-only image field. Uploads as files are chosen. */
function ImageField({ field, images, onChange, rules, email }) {
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);
  const maxCount = field.max_items;
  const maxMb = Math.round((rules?.max_bytes || 8388608) / (1024 * 1024));

  const handleFiles = async function (fileList) {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    if (!(email || "").trim()) {
      // Uploads are gated on membership, so the email has to come first.
      toast.error("Enter your GottaSecond email address above before adding images.");
      if (inputRef.current) inputRef.current.value = "";
      return;
    }
    if (images.length + files.length > maxCount) {
      toast.error(
        maxCount === 1
          ? "You can attach one image here."
          : "You can attach up to " + maxCount + " images here."
      );
      if (inputRef.current) inputRef.current.value = "";
      return;
    }
    setBusy(true);
    // Uploaded together so a multi-file selection lands in one go.
    const results = await Promise.all(
      files.map(async function (file) {
        if (file.type !== "image/jpeg") {
          toast.error(file.name + " is not a JPEG.");
          return null;
        }
        const body = new FormData();
        body.append("file", file);
        body.append("email", (email || "").trim());
        try {
          const res = await fetch(API + "/api/potw/upload", { method: "POST", body: body });
          const data = await res.json();
          if (!res.ok) throw new Error(data.detail || "Upload failed");
          return { key: data.key, url: data.url, filename: file.name };
        } catch (err) {
          toast.error(err.message || "Could not upload " + file.name);
          return null;
        }
      })
    );
    const added = results.filter(Boolean);
    if (added.length) onChange(images.concat(added));
    setBusy(false);
    if (inputRef.current) inputRef.current.value = "";
  };

  return (
    <div>
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg"
        multiple={field.max_items > 1}
        className="hidden"
        onChange={function (e) { handleFiles(e.target.files); }}
        data-testid={"potw-image-input-" + field.id}
      />
      <Button
        type="button"
        variant="outline"
        disabled={busy || images.length >= maxCount}
        onClick={function () { inputRef.current && inputRef.current.click(); }}
        className="border-white/15 bg-white/[0.03] hover:bg-white/10 text-white"
      >
        <Upload className="w-4 h-4 mr-2" />
        {busy ? "Uploading…" : field.max_items > 1 ? "Choose JPEG images" : "Choose a JPEG image"}
      </Button>
      <p className="text-xs text-white/30 mt-2">
        JPEG only, up to {maxMb} MB each. {images.length} of {maxCount} attached.
      </p>

      {images.length > 0 && (
        <ul className="mt-3 space-y-1.5" data-testid={"potw-image-list-" + field.id}>
          {images.map(function (img, i) {
            return (
              <li
                key={img.key}
                className="flex items-center gap-3 text-sm bg-white/[0.04] border border-white/10 rounded px-3 py-2"
              >
                <span className="flex-1 truncate text-white/70">{img.filename}</span>
                <button
                  type="button"
                  onClick={function () {
                    onChange(images.filter(function (_, j) { return j !== i; }));
                  }}
                  className="text-white/40 hover:text-white"
                  aria-label={"Remove " + img.filename}
                >
                  <X className="w-4 h-4" />
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

/** Renders whichever form the API describes, so labels never drift from the email. */
function SubmissionForm({ kind }) {
  const [form, setForm] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [answers, setAnswers] = useState({});
  const [images, setImages] = useState({});
  const [consents, setConsents] = useState({});
  const [honeypot, setHoneypot] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(function () {
    let cancelled = false;
    fetch(API + "/api/potw/forms/" + kind)
      .then(function (r) {
        if (r.status === 404) { if (!cancelled) setNotFound(true); return null; }
        return r.json();
      })
      .then(function (data) { if (data && !cancelled) setForm(data); })
      .catch(function () { if (!cancelled) setNotFound(true); });
    return function () { cancelled = true; };
  }, [kind]);

  const setAnswer = useCallback(function (id, value) {
    setAnswers(function (prev) { return Object.assign({}, prev, { [id]: value }); });
  }, []);

  const handleSubmit = async function (e) {
    e.preventDefault();
    setSubmitting(true);
    try {
      const imageKeys = {};
      Object.keys(images).forEach(function (fieldId) {
        imageKeys[fieldId] = images[fieldId].map(function (i) { return i.key; });
      });
      const payload = {
        answers: Object.assign({}, answers, consents),
        image_keys: imageKeys,
        website: honeypot,
      };
      const res = await fetch(API + "/api/potw/submit/" + kind, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(function () { return {}; });
      if (!res.ok) throw new Error(data.detail || "Something went wrong. Please try again.");
      setSubmitted(true);
      window.scrollTo(0, 0);
    } catch (err) {
      toast.error(err.message || "Could not send your submission.");
    } finally {
      setSubmitting(false);
    }
  };

  if (notFound) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-24 text-center">
        <h1 className="text-3xl font-bold mb-3">This form isn't open yet</h1>
        <p className="text-white/50 mb-8">
          Photographer of the Week submissions for this group aren't open right now.
        </p>
        <Link to="/photographer-of-the-week">
          <Button className="bg-white text-black hover:bg-white/90">Back to Photographer of the Week</Button>
        </Link>
      </div>
    );
  }

  if (!form) {
    return <div className="max-w-2xl mx-auto px-6 py-24 text-white/40">Loading…</div>;
  }

  if (submitted) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-24">
        <div className="text-center py-12 bg-white/5 rounded-lg border border-white/10">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h1 className="text-2xl font-semibold mb-2">Thank you — we've got it</h1>
          <p className="text-white/60 mb-6 max-w-md mx-auto">
            We read every submission. If we'd like to feature you, we'll email you before
            anything is published.
          </p>
          <Link to="/">
            <Button className="bg-white text-black hover:bg-white/90">Back to Home</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="max-w-2xl mx-auto px-6 py-24" data-testid="potw-form">
      <Link
        to="/photographer-of-the-week"
        className="inline-flex items-center gap-2 text-white/40 hover:text-white transition-colors mb-8 text-sm"
      >
        <ArrowLeft className="w-4 h-4" /> Photographer of the Week
      </Link>

      <p className="text-[11px] uppercase tracking-[3px] text-[#e8594a] font-mono mb-4">
        {form.name}
      </p>
      <h1 className="text-3xl md:text-4xl font-bold mb-4 tracking-tight">{form.title}</h1>
      <p className="text-white/50 mb-4 leading-relaxed whitespace-pre-line">{form.intro}</p>
      <p className="text-xs text-white/30 mb-12">Fields marked * are required.</p>

      {form.sections.map(function (section) {
        return (
          <section key={section.heading} className="mb-12">
            <h2 className="text-xs uppercase tracking-[2px] text-white/40 font-medium pb-2 mb-6 border-b border-white/10">
              {section.heading}
            </h2>
            {section.note && <p className="text-sm text-white/40 -mt-3 mb-6">{section.note}</p>}

            <div className="space-y-6">
              {section.fields.map(function (field) {
                if (!isVisible(field, answers)) return null;
                const fieldRequired = isRequired(field, answers);
                return (
                  <div key={field.id}>
                    <label
                      htmlFor={"f-" + field.id}
                      className="block text-sm font-medium text-white/80 mb-2"
                    >
                      {field.label}
                      {fieldRequired && <span className="text-[#e8594a] ml-1">*</span>}
                    </label>
                    {field.help && <p className="text-xs text-white/30 mb-2">{field.help}</p>}

                    {field.type === "textarea" && (
                      <Textarea
                        id={"f-" + field.id}
                        rows={4}
                        required={fieldRequired}
                        value={answers[field.id] || ""}
                        onChange={function (e) { setAnswer(field.id, e.target.value); }}
                        className="bg-white/[0.05] border-white/10 text-white"
                        data-testid={"potw-field-" + field.id}
                      />
                    )}

                    {(field.type === "text" || field.type === "email" || field.type === "url") && (
                      <Input
                        id={"f-" + field.id}
                        type={field.type === "email" ? "email" : "text"}
                        required={fieldRequired}
                        value={answers[field.id] || ""}
                        onChange={function (e) { setAnswer(field.id, e.target.value); }}
                        className="bg-white/[0.05] border-white/10 text-white"
                        data-testid={"potw-field-" + field.id}
                      />
                    )}

                    {field.type === "select" && (
                      <select
                        id={"f-" + field.id}
                        required={fieldRequired}
                        value={answers[field.id] || ""}
                        onChange={function (e) { setAnswer(field.id, e.target.value); }}
                        className="w-full bg-white/[0.05] border border-white/10 text-white h-10 rounded-md px-3 text-sm"
                        data-testid={"potw-field-" + field.id}
                      >
                        <option value="">Select…</option>
                        {field.options.map(function (opt) {
                          return <option key={opt} value={opt}>{opt}</option>;
                        })}
                      </select>
                    )}

                    {field.type === "multi" && (
                      <div className="space-y-2" data-testid={"potw-field-" + field.id}>
                        {field.options.map(function (opt) {
                          const selected = (answers[field.id] || []).indexOf(opt) !== -1;
                          return (
                            <label key={opt} className="flex items-center gap-3 text-sm text-white/70 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={selected}
                                onChange={function () {
                                  const current = answers[field.id] || [];
                                  setAnswer(
                                    field.id,
                                    selected
                                      ? current.filter(function (v) { return v !== opt; })
                                      : current.concat([opt])
                                  );
                                }}
                                className="accent-[#e8594a] w-4 h-4"
                              />
                              {opt}
                            </label>
                          );
                        })}
                      </div>
                    )}

                    {field.type === "images" && (
                      <ImageField
                        field={field}
                        images={images[field.id] || []}
                        onChange={function (next) {
                          setImages(function (prev) {
                            return Object.assign({}, prev, { [field.id]: next });
                          });
                        }}
                        rules={form.image_rules}
                        email={answers.email}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        );
      })}

      {/* Honeypot - hidden from people, filled in by bots */}
      <input
        type="text"
        name="website"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        value={honeypot}
        onChange={function (e) { setHoneypot(e.target.value); }}
        style={{ position: "absolute", left: "-9999px", width: 1, height: 1 }}
      />

      <div className="space-y-3 mb-8">
        {form.consents.map(function (consent) {
          return (
            <label
              key={consent.id}
              className="flex gap-3 items-start bg-white/[0.03] border border-white/10 rounded-lg p-4 cursor-pointer"
            >
              <input
                type="checkbox"
                required={consent.required}
                checked={consents[consent.id] === true}
                onChange={function (e) {
                  const checked = e.target.checked;
                  setConsents(function (prev) {
                    return Object.assign({}, prev, { [consent.id]: checked });
                  });
                }}
                className="accent-[#e8594a] w-4 h-4 mt-0.5 shrink-0"
                data-testid={"potw-consent-" + consent.id}
              />
              <span className="text-sm text-white/70 leading-relaxed">
                {consent.label}
                {consent.required && <span className="text-[#e8594a] ml-1">*</span>}
              </span>
            </label>
          );
        })}
      </div>

      <Button
        type="submit"
        disabled={submitting}
        className="bg-white text-black hover:bg-white/90 w-full sm:w-auto px-8"
        data-testid="potw-submit"
      >
        {submitting ? "Sending…" : "Submit"}
      </Button>
    </form>
  );
}

export default function PhotographerOfTheWeekPage() {
  const { kind } = useParams();

  const seoTitle = kind && FORM_KINDS[kind]
    ? "Photographer of the Week — " + FORM_KINDS[kind].seoName
    : "Photographer of the Week";

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white" data-testid="potw-page">
      <SEO
        title={seoTitle}
        description="Submit your work to be featured in GottaSecond's Photographer of the Week series — for second shooters, assistants, and studios."
        path={"/photographer-of-the-week" + (kind ? "/" + kind : "")}
      />
      <NavBar>
        <Link to="/pricing" className="text-sm font-medium text-white/60 hover:text-white transition-colors uppercase tracking-wider">Pricing</Link>
        <Link to="/login" className="text-sm font-medium text-white/60 hover:text-white transition-colors uppercase tracking-wider">Login</Link>
        <Link to="/register" className="text-sm font-medium text-[#e8594a] hover:text-[#ff6b5a] transition-colors uppercase tracking-wider font-bold">Get Started</Link>
      </NavBar>

      {kind ? <SubmissionForm kind={kind} /> : <ChooseForm />}
    </div>
  );
}
